import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ArticleDetail.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function ArticleDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/articles/${id}`)
      .then(r => r.json())
      .then(d => { setArticle(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="container"><div className="loading"><div className="spinner"></div></div></div>;
  if (!article) return <div className="container"><div className="error"><Link to="/">Back</Link></div></div>;

  const score = Math.round(article.confidence_score * 100);
  const level = score >= 85 ? 'high' : score >= 60 ? 'medium' : 'low';
  const label = score >= 85 ? 'Verified' : score >= 60 ? 'Emerging' : 'Unverified';

  // Format article content into proper paragraphs
  const formatArticleContent = (content) => {
    if (!content) return [];
    
    // Split by double newlines for paragraphs
    let paragraphs = content.split('\n\n');
    
    // If no double newlines, try single newlines
    if (paragraphs.length === 1) {
      paragraphs = content.split('\n').filter(p => p.trim());
    }
    
    return paragraphs.filter(p => p.trim()).map(p => p.trim());
  };

  const paragraphs = formatArticleContent(article.content);

  return (
    <div className="container">
      <div className="article-detail">
        <Link to="/" className="back-link">← Back to Feed</Link>

        <div className="article-header">
          <div className="header-badges">
            <div className="ai-badge"><span>✨ AI-Generated</span></div>
            <div className={`confidence-badge ${level}`}>{label} {score}%</div>
          </div>
          <h1 className="article-title">{article.title}</h1>
          <div className="article-metadata">
            <span>📅 {new Date(article.generated_at).toLocaleString()}</span>
            {article.sources && <span>📊 {article.sources.length} sources</span>}
          </div>
        </div>

        <div className="confidence-section card">
          <h3>Confidence Score</h3>
          <div className="confidence-bar-container">
            <div className={`confidence-bar ${level}`} style={{width: `${score}%`}}>
              <span>{score}%</span>
            </div>
          </div>
          <p>Based on source credibility and multi-agent fact-checking.</p>
        </div>

        <div className="article-content card">
          <div className="article-body">
            {paragraphs.map((p, i) => {
              // Check if this is a headline (starts with ** or is all caps short text)
              if (p.startsWith('**') && p.endsWith('**')) {
                const headline = p.replace(/\*\*/g, '');
                return <h2 key={i} className="article-headline">{headline}</h2>;
              }
              
              // Check for subheadings (single line, short, ends with colon or question mark)
              if (p.length < 100 && (p.endsWith(':') || p.endsWith('?'))) {
                return <h3 key={i} className="article-subheading">{p}</h3>;
              }
              
              // Check for quotes (contains quotation marks)
              if (p.includes('"') || p.includes('"') || p.includes('"')) {
                return <blockquote key={i} className="article-quote">{p}</blockquote>;
              }
              
              // Regular paragraph
              return <p key={i} className="article-paragraph">{p}</p>;
            })}
          </div>
        </div>

        {article.sources && article.sources.length > 0 && (
          <div className="source-trail card">
            <h3>🔍 Source Trail</h3>
            <p className="section-description">Original sources used to generate this article.</p>
            <div className="sources-list">
              {article.sources.map((s, i) => (
                <div key={i} className="source-item">
                  <div className="source-header">
                    <span className="platform-badge">{s.platform === 'reddit' ? '🔴' : '🔵'} {s.platform}</span>
                    <span>by {s.author}</span>
                  </div>
                  <p>{s.content_text}</p>
                  {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer">View Original ↗</a>}
                </div>
              ))}
            </div>
          </div>
        )}

        {article.editor_analysis && (
          <div className="multi-agent-process card">
            <h3>🤖 Multi-Agent Process</h3>
            <div className="agent-steps">
              <div className="agent-step">
                <div><strong>🎬 Editor:</strong></div>
                <p>{article.editor_analysis}</p>
              </div>
              {article.research_context && (
                <div className="agent-step">
                  <div><strong>🔍 Researcher:</strong></div>
                  <p>{article.research_context}</p>
                </div>
              )}
              {article.fact_check && (
                <div className="agent-step">
                  <div><strong>✅ Fact-Checker:</strong></div>
                  <p>{article.fact_check}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ArticleDetail;
